import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Breadcrumb from '../../../component/common/Breadcrumb';
import WorkoutPlanDetailsTab from '../../../component/HealthComponent/WorkoutPlanForm/WorkoutPlanDetailsTab';
import ScheduleTab from '../../../component/HealthComponent/WorkoutPlanForm/ScheduleTab';
import ViewWorkoutPlans from './viewWorkoutPlans';
import { getLocalStorageItem, successToast, errorToast } from '../../../utils/helper';
import { getWorkoutById, getWorkoutPlanById } from '../../../api/healthAPI';
import {
  WorkoutPlanContextProvider,
  useWorkoutPlanCreation
} from '../../../context/WorkoutPlanContext';

const NewWorkoutPlansContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const propsData = location?.state;
  const id = new URLSearchParams(window.location.search).get('id');
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));

  // Use context
  const {
    planData,
    updatePlanData,
    saveDetailsStep,
    saveScheduleStep,
    setCurrentTab,
    currentTab,
    isLoading,
    validateDetailsTab
  } = useWorkoutPlanCreation();

  // View mode and preview state
  const [isView, setIsView] = useState(false);
  const [isPreview, setIsPreview] = useState(false);

  // Breadcrumb
  const [pages] = useState([
    {
      name: 'Health Dashboard',
      href: `${userData?.slug ? `/${userData.slug}` : ''}/health/dashboard`
    },
    {
      name: 'Workout Plans',
      href: `${userData?.slug ? `/${userData.slug}` : ''}/health/workout-plans`
    },
    { name: id ? 'Edit Workout Plan' : 'New Workout Plan', current: true }
  ]);

  // Fetch workout plan data for editing
  const fetchWorkoutPlanData = async (planId) => {
    try {
      const response = await getWorkoutPlanById(planId);

      if (response?.meta?.code === 1 && response?.data) {
        const planDataFromApi = response.data;

        updatePlanData({
          plan_name: planDataFromApi.plan_name || '',
          plan_description: planDataFromApi.plan_description || '',
          workout_focus: planDataFromApi.workout_focus || '',
          location: planDataFromApi.location || '',
          difficulty: planDataFromApi.difficulty || '',
          thumbnail_url: planDataFromApi.thumbnail_url || '',
          current_step: planDataFromApi.current_step || 1,
          status: planDataFromApi.status || 'draft',
          schedule:
            planDataFromApi.days?.map((day, index) => ({
              day_number: day.day_sequence || index + 1,
              workouts: day.workout_ids || []
            })) || [],
          workoutPlanId: planDataFromApi._id
        });
      }
    } catch (err) {
      console.error('Error fetching workout plan data:', err);
      errorToast('Failed to load workout plan data');
    }
  };

  // Load data if editing
  useEffect(() => {
    if (id || propsData?.id) {
      const planId = id || propsData.id;
      if (propsData?.action === 'view') {
        setIsView(true);
      }
      fetchWorkoutPlanData(planId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, propsData]);

  // Handle submit for Details tab (Save and Continue)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // If on Details tab, save details and move to Schedule tab
    if (currentTab === 'details') {
      const result = await saveDetailsStep();

      if (result.success) {
        // Move to Schedule tab
        setCurrentTab('schedule');
      }
    }
  };

  // Handle Publish
  const handlePublish = async (e) => {
    e.preventDefault();

    const result = await saveScheduleStep('published');

    if (result.success) {
      navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/workout-plans`);
    }
  };

  // Handle Save as Draft
  const handleSaveAsDraft = async (e) => {
    e.preventDefault();

    const result = await saveScheduleStep('draft');

    if (result.success) {
      successToast('Workout plan saved as draft successfully!');
      navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/workout-plans`);
    }
  };

  // Tab navigation handler
  const handleTabKey = (e, tab) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setCurrentTab(tab);
    }
  };

  // If in preview mode, show ViewWorkoutPlans component
  if (isPreview) {
    return <ViewWorkoutPlans isPreview={true} onExitPreview={() => setIsPreview(false)} />;
  }

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{id ? 'Edit Workout Plan' : 'New Workout Plan'} | Shoorah Admin</title>
      </Helmet>

      <Breadcrumb pageList={pages} className="lg:mt-8" />

      {/* Preview Button - Only visible on Schedule tab */}
      {!isView && currentTab === 'schedule' && (
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={() => setIsPreview(true)}
            disabled={isLoading}
            className="px-8 py-2 border border-gray-300 bg-white text-gray-700 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Preview
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <nav
        className="border-b mt-6 bg-white dark:bg-shoorah-darkBgTabColor rounded-t-lg"
        role="tablist"
        aria-label="Workout Plan tabs"
      >
        <ul className="flex gap-6 px-6">
          <li>
            <button
              type="button"
              role="tab"
              aria-selected={currentTab === 'details'}
              tabIndex={currentTab === 'details' ? 0 : -1}
              onClick={() => setCurrentTab('details')}
              onKeyDown={(e) => handleTabKey(e, 'details')}
              className={`px-1 pb-3 pt-4 text-base ${
                currentTab === 'details'
                  ? 'text-shoorah-primary border-b-2 border-shoorah-primary font-medium'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              Details
            </button>
          </li>
          <li>
            <button
              type="button"
              role="tab"
              aria-selected={currentTab === 'schedule'}
              tabIndex={currentTab === 'schedule' ? 0 : -1}
              onClick={() => setCurrentTab('schedule')}
              onKeyDown={(e) => handleTabKey(e, 'schedule')}
              className={`px-1 pb-3 pt-4 text-base ${
                currentTab === 'schedule'
                  ? 'text-shoorah-primary border-b-2 border-shoorah-primary font-medium'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              Schedule
            </button>
          </li>
        </ul>
      </nav>

      {/* Tab Content */}
      <div className="mt-6 space-y-6">
        {currentTab === 'details' && (
          <div role="tabpanel" aria-labelledby="details-tab">
            <WorkoutPlanDetailsTab isView={isView} />
          </div>
        )}

        {currentTab === 'schedule' && (
          <div role="tabpanel" aria-labelledby="schedule-tab">
            <ScheduleTab isView={isView} />
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {!isView && (
        <div className="mt-8 flex justify-start gap-4 dark:bg-shoorah-darkBgTabColor rounded-lg ">
          {currentTab === 'details' ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Saving...' : id ? 'Update' : 'Save & Continue'}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleSaveAsDraft}
                disabled={isLoading}
                className="px-8 py-2 border border-gray-300 bg-white text-gray-700 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : 'Save as Draft'}
              </button>
              <button
                type="button"
                onClick={handlePublish}
                disabled={isLoading}
                className="btn-primary"
              >
                {isLoading ? 'Publishing...' : 'Publish'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Main component with context provider
const NewWorkoutPlans = () => {
  return (
    <WorkoutPlanContextProvider>
      <NewWorkoutPlansContent />
    </WorkoutPlanContextProvider>
  );
};

export default NewWorkoutPlans;
